import * as angular from 'angular';
import { noop } from 'angular';
import { UserService } from 'go-modules/models/user/user.service';
import { MessageModal } from 'go-modules/modals/message/modal.factory';
import { DowngradeModalService } from '@go-modules/services/downgrade-modal/downgrade-modal.service';
import { EditUserDialogComponent } from '@go-modules/components/dialogs/edit-user/edit-user-dialog.component';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { UserRoleLocalizationUtil } from '@go-modules/utilities/user-role-localization/user-role-localization.util';
import { RouterService } from '@go-modules/services/router/router.service';
import { NgxInviteService } from '@go-modules/services/invite/invite.service';
import type { Invite } from '@go-modules/interfaces/invite/invite';

/* @ngInject */
export class ManageUsersController implements ng.IController {
	public invites = {};
	public currentInvites = [] as Invite[];
	public selectedInvite = null;
	public selectedUser = null;

	constructor (
		private $scope,
		private $log: angular.ILogService,
		private Group,
		private UserGroupModel,
		private messageModal: MessageModal,
		private ngxInviteService: NgxInviteService,
		private userService: UserService,
		private $translate: ng.translate.ITranslateService,
		private ngxDowngradeModalService: DowngradeModalService,
		private ngxGoModalService: GoModalService,
		private ngxRouterService: RouterService
	) {}

	public $onInit (): void {
		// check for user in route
		if (this.ngxRouterService.getCurrentQueryParams().userId) {
			this.$scope.group.getDataPromise.then(() => {
				const user = this.$scope.group.users.find((u) => {
					return u.user_id === this.ngxRouterService.getCurrentQueryParams().userId;
				});

				this.selectUser(user);

				// check action
				if (user != null && this.ngxRouterService.getCurrentQueryParams().action === 'remove') {
					this.openRemoveUser(this.selectedUser);
				}
			}).catch(this.$log.error);
		}

		// load invites
		this.setInvites(this.getGroupId());
	}

	public getGroupId = (): number => {
		return this.$scope.group ?
			this.$scope.group.group_id :
			Number(this.ngxRouterService.getCurrentParams().folder_id);
	};

	/** Select a user */
	public selectUser = (user) => {
		if (this.selectedUser === user) {
			return user;
		}
		this.selectedUser = user;
		return user;
	};

	/**
	 * User sort function
	 *
	 * @param user
	 * @returns number
	 */
	public userSort = (user) => {
		if (!user || !user.role) {
			return false;
		}
		return this.Group.rolePrecedence[user.role];
	};

	/** Select an invite */
	public selectInvite = (invite: Invite): Invite => {
		if (this.selectedInvite === invite) {
			return;
		}
		this.selectedInvite = invite;
		return invite;
	};

	/** Resend an invite */
	public resendInvite = (invite: Invite): void => {
		this.ngxInviteService.resend(invite.groupId, invite.inviteId)
			.subscribe(() => invite.lastSent = new Date());
	};

	// open remove invite modal
	public openRemoveInvite = (invite: Invite): ng.IPromise<any> => {
		return this.ngxDowngradeModalService.openConfirmDialog({
			title: this.$translate.instant('modal-remove-invite_remove'),
			message: this.$translate.instant('modal-remove-invite_sure-delete-invite', {email: invite.email}),
			confirmText: this.$translate.instant('common_remove')
		}).then(() => {
			this.removeInvite(invite);
		}).catch(noop);
	};

	// Check if the user can be removed
	// This check is done when the user presses the remove user option because this endpoint is expensive.
	public checkIfCanRemoveUser = (user) => {
		if (this.userService.currentUser.is_root_user) {
			this.openRemoveUser(user);
			return;
		}

		this.UserGroupModel.getCanRemoveUser({ group_id: this.$scope.group.group_id, user_id: user.user_id })
			.$promise.then((result) => {
				return result.can_remove ?
					this.openRemoveUser(user) :
					this.openDeactivateUser(user, true);
			});
	};

	// open block user modal
	public openDeactivateUser = (user, redirected: boolean = false): ng.IPromise<any> => {
		return this.ngxDowngradeModalService.openConfirmDialog({
			title: this.$translate.instant('modal-deactivate-user_title'),
			message: redirected ?
				this.$translate.instant('modal-redirected-to-deactivate-user_sure-deactivate-user', {user: user.fullname, group: this.$scope.group.name}) :
				this.$translate.instant('modal-deactivate-user_sure-deactivate-user', {user: user.fullname, group: this.$scope.group.name}),
			confirmText: this.$translate.instant('modal-deactivate-user_deactivate-button')
		}).then(() => {
			this.deactivateUserInGroup(user, this.$scope.group);
		}).catch(noop);
	};

	// open remove user modal
	public openRemoveUser = (user): ng.IPromise<any> => {
		let note = null;
		if (user.role === 'presenter' && user.billing_entity_type === 'user') {
			note = this.$translate.instant('modal-remove-user_credit-will-be-given');
		}
		return this.ngxDowngradeModalService.openConfirmDialog({
			title: this.$translate.instant('modal-remove-user_remove-user'),
			message: this.$translate.instant('modal-remove-user_sure-remove-user', {user: user.fullname, group: this.$scope.group.name}),
			confirmText: this.$translate.instant('common_remove'),
			note
		}).then(() => {
			this.removeUserFromGroup(user, this.$scope.group);
		}).catch(noop);
	};

	// open edit User
	public openEditUser = (user): void => {
		this.ngxGoModalService.open(
			EditUserDialogComponent,
			false,
			{
				data: {
					user,
					group: this.$scope.group
				}
			}
		).afterClosed().subscribe(({role, shouldClose}) => {
			if (role) {
				user.role = role;
				user.role_localized = UserRoleLocalizationUtil.localize(user.role);
			}
			if (shouldClose) {
				this.$scope.$close();
			}
		});
	};

	// open invite modal
	public openInviteModal = (group): void => {
		this.ngxDowngradeModalService.inviteUsersDialog({
			group
		}).then((invites: Invite[]) => {
			this.currentInvites = this.currentInvites || [];
			this.currentInvites = this.currentInvites.concat(invites);
			this.$scope.group.getData();
		}).catch(this.$log.error);
	};

	public reactivateUserToGroup = (user, group): ng.IPromise<any> => {
		const oldDeletedAt = user.deleted_at;

		// Move the user back into the undeleted section
		delete user.deleted_at;

		// undelete their paid user role
		return group.reactivateUser(user).catch((err) => {
			// If the request fails, put them back in the deleted list
			user.deleted_at = oldDeletedAt;
			this.$log.error(err);

			// And inform the user it failed
			this.messageModal.open({
				modalData: {
					title: 'group-settings_users-management-failed-title',
					message: 'group-settings_users-management-failed-message'
				}
			});
		});
	};

	private deactivateUserInGroup = (user, group): ng.IPromise<any> => {
		// This won't match the DB exactly, but that's okay.
		// We just need to inform angularjs to move this to the deactivated section
		user.deleted_at = (new Date()).toISOString();

		// Delete their user role without marking unpaid
		return group.deactivateUser(user).catch((err) => {
			// If the request fails, put them back in the active list
			delete user.deleted_at;
			this.$log.error(err);

			// And inform the user it failed
			this.messageModal.open({
				modalData: {
					title: 'group-settings_users-management-failed-title',
					message: 'group-settings_users-management-failed-message'
				}
			});
		});
	};

	/** Remove user from a group */
	private removeUserFromGroup = (user, group): ng.IPromise<any> => {
		const oldBillingEntityId = user.billing_entity_id;

		// This won't match the DB exactly, but that's okay.
		// We just need to inform angularjs to move this to the deleted section
		user.deleted_at = (new Date()).toISOString();
		delete user.billing_entity_id;

		// delete user from group
		return group.deleteUser(user).catch((err) => {
			// If the request fails, put them back in the active list
			delete user.deleted_at;
			user.billing_entity_id = oldBillingEntityId;
			this.$log.error(err);

			// And inform the user it failed
			this.messageModal.open({
				modalData: {
					title: 'group-settings_users-management-failed-title',
					message: 'group-settings_users-management-failed-message'
				}
			});
		});
	};

	/** Set the invites for the current group */
	private setInvites = (groupId): void => {
		if (groupId) {
			if (!this.invites[groupId]) {
				this.ngxInviteService.invites(groupId)
					.subscribe((result) => {
						const invites = this.ngxInviteService.normalizeInvites(result);
						this.invites[groupId] = invites;
						this.currentInvites = invites;
					});
			} else if (!this.ngxRouterService.getCurrentQueryParams().profile) {
				this.currentInvites = this.invites[groupId];
			}
		} else {
			this.currentInvites = [];
		}
	};

	/** Remove an invite */
	private removeInvite = (invite: Invite): void => {
		this.ngxInviteService.deleteInvite(invite.groupId, invite.inviteId)
			.subscribe(() => {
				this.currentInvites.splice(this.currentInvites.indexOf(invite), 1);
			});
	};
}
